import { useState, type FormEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useLiveQuery } from 'dexie-react-hooks'
import { Page } from '@/components/Page'
import { formatKarachiDateTime } from '@/lib/dates'
import { formatPKR, grossProfit, pkrToPaisa, subPaisa } from '@/lib/money'
import { openWorkspaceDb } from '@/offline/db'
import { getActiveWorkspaceId } from '@/features/workspace/workspace'
import { buildReceiptText, type ReceiptInfo } from '@/features/share/shareText'
import { renderReceiptPng } from '@/features/share/receiptImage'
import { openWhatsApp, openWhatsAppTo, shareImageFile } from '@/features/share/share'
import { buildSale } from './salePayload'
import { saveSale } from './saveSale'

const inputCls =
  'w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-sky-500 focus:outline-none'
const labelCls = 'mb-1 block text-xs font-medium text-slate-400'

interface DoneSale {
  amount: number
  received: number
  soldAt: string
  customerName: string
  customerPhone: string
}

/** Sell an in-stock device: atomic sale + double-sell lock + profit + receivable + receipt. */
export function SellPage() {
  const { deviceId = '' } = useParams()
  const ws = getActiveWorkspaceId()
  const navigate = useNavigate()

  const device = useLiveQuery(
    async () => (ws && deviceId ? openWorkspaceDb(ws).devices.get(deviceId) : undefined),
    [ws, deviceId],
  )
  const purchase = useLiveQuery(
    async () =>
      ws && deviceId ? openWorkspaceDb(ws).purchases.where('device_id').equals(deviceId).first() : undefined,
    [ws, deviceId],
  )
  const imeis = useLiveQuery(
    async () =>
      ws && deviceId ? openWorkspaceDb(ws).device_imeis.where('device_id').equals(deviceId).toArray() : [],
    [ws, deviceId],
  )

  const [amount, setAmount] = useState('')
  const [received, setReceived] = useState('')
  const [payMode, setPayMode] = useState<'full' | 'partial'>('full')
  const [customerName, setCustomerName] = useState('')
  const [customerPhone, setCustomerPhone] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [doneSale, setDoneSale] = useState<DoneSale | null>(null)

  if (!device) return <Page title="Sell" note="Device not found." />
  if (device.status !== 'in_stock') return <Page title="Sell" note="This device is already sold or returned." />

  const cost = (purchase?.cost_paisa ?? 0) + (purchase?.direct_cost_paisa ?? 0)

  let amountPaisa = 0
  let receivedPaisa = 0
  let amountOk = false
  try {
    amountPaisa = pkrToPaisa(amount)
    amountOk = true
  } catch {
    amountOk = false
  }
  if (payMode === 'full') {
    // One-step sale: fully paid by default, so a cash sale creates no receivable / extra step.
    receivedPaisa = amountOk ? amountPaisa : 0
  } else {
    try {
      receivedPaisa = received.trim() ? pkrToPaisa(received) : 0
    } catch {
      receivedPaisa = 0
    }
  }
  const profit = amountOk ? grossProfit(amountPaisa, cost, []) : 0
  const receivable = amountOk ? subPaisa(amountPaisa, receivedPaisa) : 0

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (!ws || !purchase) return setError('Missing workspace or purchase record.')
    if (!amountOk || amountPaisa <= 0) return setError('Enter a valid sale amount.')
    if (receivedPaisa < 0 || receivedPaisa > amountPaisa)
      return setError('Received cannot exceed the sale amount.')

    const built = buildSale({
      workspace_id: ws,
      device: device!,
      purchase_id: purchase.id,
      sale_amount_paisa: amountPaisa,
      received_paisa: receivedPaisa,
      customer_name: customerName || null,
      customer_phone: customerPhone || null,
    })

    setBusy(true)
    try {
      const summary = await saveSale(ws, built)
      if (summary.rejected.length > 0) {
        const st = summary.rejected[0].result.status
        setError(st === 'already_sold' ? 'This device was already sold (double-sell blocked).' : `Not saved: ${st}`)
        return
      }
      setDoneSale({
        amount: built.sale.sale_amount_paisa,
        received: built.sale.received_paisa,
        soldAt: built.sale.sold_at,
        customerName: customerName.trim(),
        customerPhone: customerPhone.trim(),
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  if (doneSale) {
    const info: ReceiptInfo = {
      model: device.model,
      imeis: (imeis ?? []).map((i) => i.imei_normalized),
      saleAmount: doneSale.amount,
      received: doneSale.received,
      balance: subPaisa(doneSale.amount, doneSale.received),
      soldAt: doneSale.soldAt,
      customer: doneSale.customerName || null,
    }
    const receiptText = buildReceiptText(info)
    const fname = `receipt-${device.model.replace(/\s+/g, '-')}.png`

    async function shareImage() {
      try {
        const blob = await renderReceiptPng(info)
        const ok = await shareImageFile(blob, fname)
        if (!ok) {
          const url = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = fname
          a.click()
          URL.revokeObjectURL(url)
        }
      } catch {
        /* canvas/share unavailable — text share still offered */
      }
    }

    return (
      <Page title="Sale saved" note={`Profit ${formatPKR(grossProfit(doneSale.amount, cost, []))}`}>
        <ReceiptCard info={info} />
        <div className="mt-3 space-y-2">
          <button
            type="button"
            onClick={() =>
              doneSale.customerPhone ? openWhatsAppTo(doneSale.customerPhone, receiptText) : openWhatsApp(receiptText)
            }
            className="w-full rounded-xl bg-emerald-500 px-4 py-3 text-base font-semibold text-white"
          >
            {doneSale.customerPhone ? `Send receipt to ${doneSale.customerPhone} (WhatsApp)` : 'Share receipt on WhatsApp'}
          </button>
          <button
            type="button"
            onClick={() => void shareImage()}
            className="w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-base font-medium text-slate-200"
          >
            Share receipt image
          </button>
          <button
            type="button"
            onClick={() => navigate('/inventory')}
            className="w-full rounded-xl border border-slate-700 px-4 py-3 text-base font-medium text-slate-300"
          >
            View stock
          </button>
        </div>
      </Page>
    )
  }

  return (
    <Page title={`Sell — ${device.model}`} note={`Cost ${formatPKR(cost)} (incl. extra cost)`}>
      <form onSubmit={onSubmit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls} htmlFor="cname">Customer name</label>
            <input id="cname" className={inputCls} value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="optional" />
          </div>
          <div>
            <label className={labelCls} htmlFor="cphone">Customer WhatsApp number</label>
            <input id="cphone" className={inputCls} inputMode="tel" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} placeholder="0300…" />
          </div>
        </div>
        <div>
          <label className={labelCls} htmlFor="amount">Sale amount (PKR) *</label>
          <input id="amount" className={inputCls} inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="115000" autoFocus />
        </div>
        <div>
          <p className={labelCls}>Payment</p>
          <div className="mb-2 grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setPayMode('full')}
              className={`rounded-lg px-3 py-2 text-sm font-semibold ${payMode === 'full' ? 'bg-emerald-500 text-white' : 'border border-slate-700 bg-slate-900 text-slate-300'}`}
            >
              Full payment
            </button>
            <button
              type="button"
              onClick={() => setPayMode('partial')}
              className={`rounded-lg px-3 py-2 text-sm font-semibold ${payMode === 'partial' ? 'bg-amber-500 text-white' : 'border border-slate-700 bg-slate-900 text-slate-300'}`}
            >
              Partial / udhaar
            </button>
          </div>
          {payMode === 'partial' ? (
            <div>
              <label className={labelCls} htmlFor="received">Received now (PKR)</label>
              <input id="received" className={inputCls} inputMode="decimal" value={received} onChange={(e) => setReceived(e.target.value)} placeholder="0 = full udhaar" />
            </div>
          ) : (
            <p className="text-xs text-slate-500">Full amount received — sale complete in one step, no udhaar entry.</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl border border-slate-800 bg-slate-900/40 px-3 py-2.5">
            <p className="text-[11px] uppercase tracking-wide text-slate-500">Profit</p>
            <p className={`mt-0.5 text-lg font-semibold tabular-nums ${profit > 0 ? 'text-emerald-400' : profit < 0 ? 'text-rose-400' : 'text-slate-100'}`}>
              {amountOk ? formatPKR(profit) : '—'}
            </p>
          </div>
          <div className="rounded-xl border border-slate-800 bg-slate-900/40 px-3 py-2.5">
            <p className="text-[11px] uppercase tracking-wide text-slate-500">Receivable</p>
            <p className={`mt-0.5 text-lg font-semibold tabular-nums ${receivable > 0 ? 'text-amber-400' : 'text-slate-100'}`}>
              {amountOk ? formatPKR(receivable) : '—'}
            </p>
          </div>
        </div>

        {error ? (
          <p role="alert" className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">
            {error}
          </p>
        ) : null}

        <button type="submit" disabled={busy} className="w-full rounded-xl bg-emerald-500 px-4 py-3 text-base font-semibold text-white disabled:opacity-60">
          {busy ? 'Saving…' : 'Confirm sale'}
        </button>
      </form>
    </Page>
  )
}

/** Designed on-screen receipt template (mirrors the shared PNG). */
function ReceiptCard({ info }: { info: ReceiptInfo }) {
  return (
    <div className="rounded-xl bg-white p-4 text-slate-900 shadow">
      <p className="text-center text-lg font-bold uppercase tracking-wide">
        {info.shopName?.trim() ? info.shopName.trim() : 'Mobile Trading Pro'}
      </p>
      <p className="text-center text-xs text-slate-500">SALES RECEIPT</p>
      <div className="my-2 border-t-2 border-dashed border-slate-400" />
      <dl className="space-y-1 text-sm">
        <Row l="Item" v={info.model} strong />
        {info.imeis.map((im, i) => (
          <Row key={im} l={`IMEI ${i + 1}`} v={im} />
        ))}
        <Row l="Customer" v={info.customer?.trim() ? info.customer : 'Walk-in'} />
        <Row l="Date" v={formatKarachiDateTime(info.soldAt)} />
        <Row l="Amount" v={formatPKR(info.saleAmount)} strong />
        <Row l="Received" v={formatPKR(info.received)} />
        {info.balance > 0 ? (
          <Row l="Balance (udhaar)" v={formatPKR(info.balance)} strong red />
        ) : (
          <Row l="Balance" v="Paid in full" strong green />
        )}
      </dl>
      <div className="my-2 border-t-2 border-dashed border-slate-400" />
      <p className="text-center text-[11px] text-slate-500">
        Shukriya! Keep this receipt for warranty / return reference.
      </p>
    </div>
  )
}

function Row({ l, v, strong, red, green }: { l: string; v: string; strong?: boolean; red?: boolean; green?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-slate-500">{l}</dt>
      <dd className={`tabular-nums ${strong ? 'font-bold' : 'font-medium'} ${red ? 'text-red-600' : green ? 'text-green-600' : 'text-slate-900'}`}>
        {v}
      </dd>
    </div>
  )
}
